import axios from 'axios'
import {FC, FormEvent, useEffect, useState} from 'react'
import {Link, Navigate, useNavigate} from 'react-router-dom'
import {API_URL} from '../../../../../_metronic/helpers'
import {SUPPORT_PHONE, SUPPORT_PHONE_LABEL} from '../../../../../_metronic/helpers/supportContact'
import {useAuth} from '../../core/Auth'
import {AuthModel} from '../../core/_models'
import {getUserByToken} from '../../core/_requests'
import {useRegistrationView} from '../../core/RegisterationProvider'
import {canUseTicketing} from '../../core/ticketingAccess'
import {
  FISCAL_ID_GUIDE_URL,
  SsoRegistrationFailure,
  isCompleteFiscalId,
  isSsoSession,
  needsSsoBusinessRegistration,
  normalizeFiscalIdInput,
  readSsoRegistrationFailure,
} from '../../core/ssoBusinessRegistration'
import {ONLINE_REGISTRATION_PATH} from '../../core/onlineRegistration'
import {RegistrationSignOut} from './RegistrationSignOut'
import {createIdempotencyKey} from './businessRegistration'
import {TermsViewerButton} from '../../../terms/components/TermsViewerButton'
import {TermsDocument} from '../../../terms/core/_models'
import {acceptTerms, getCurrentTerms} from '../../../terms/core/_requests'
import {formatTermsVersion} from '../../../terms/core/termsFormat'
import './BusinessRegistration.scss'

interface SsoInquiryResult {
  inquiryId: string
  expiresAtUtc: string
  taxpayerTypeTitle: string
  taxpayerName: string
  economicCode: string
  nationalIdentifier: string
  fiscalId: string
}

interface SsoCreateResult {
  companyId: number
  companyName: string
}

const SsoBusinessRegistration: FC = () => {
  const {currentUser, saveAuth, setCurrentUser} = useAuth()
  const navigate = useNavigate()
  const {setHelpPlace} = useRegistrationView()

  const [fiscalId, setFiscalId] = useState('')
  const [inquiry, setInquiry] = useState<SsoInquiryResult>()
  const [failure, setFailure] = useState<SsoRegistrationFailure>()
  const [isInquiring, setIsInquiring] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSwitching, setIsSwitching] = useState(false)
  const [created, setCreated] = useState<SsoCreateResult>()
  const [idempotencyKey, setIdempotencyKey] = useState(createIdempotencyKey)
  const [verificationNow, setVerificationNow] = useState(Date.now)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [terms, setTerms] = useState<TermsDocument>()

  const inquiryExpired = Boolean(
    inquiry && new Date(inquiry.expiresAtUtc).getTime() <= verificationNow
  )
  const hasNoBusiness = needsSsoBusinessRegistration(currentUser)
  const busy = isInquiring || isSubmitting || isSwitching

  // The version and text the checkbox below accepts. Without it the business is still registered
  // and the acceptance window asks for the terms on entering the panel.
  useEffect(() => {
    let active = true
    getCurrentTerms()
      .then((document) => {
        if (active) setTerms(document)
      })
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (!inquiry) return
    const delay = Math.max(0, new Date(inquiry.expiresAtUtc).getTime() - Date.now())
    const timer = window.setTimeout(() => setVerificationNow(Date.now()), delay + 50)
    return () => window.clearTimeout(timer)
  }, [inquiry])

  useEffect(() => {
    setHelpPlace(
      <div className='business-registration-help'>
        <i className='las la-landmark' aria-hidden='true' />
        <h2>ثبت با کارپوشه مالیاتی</h2>
        <p>
          هویت شما از کارپوشه تأیید شده است. فقط حافظه مالیاتی‌ای ثبت می‌شود که به نام همین هویت
          باشد؛ سایر مشخصات از استعلام رسمی خوانده می‌شود.
        </p>
      </div>
    )
  }, [setHelpPlace])

  if (!isSsoSession(currentUser)) {
    return <Navigate to='/dashboard' replace />
  }

  const resetInquiry = () => {
    setInquiry(undefined)
    setFailure(undefined)
    setIdempotencyKey(createIdempotencyKey())
  }

  const inquire = async () => {
    if (!isCompleteFiscalId(fiscalId)) {
      setFailure({
        message: 'شناسه حافظه مالیاتی باید ۶ کاراکتر (حروف بزرگ انگلیسی و رقم) باشد.',
        requiresFreshInquiry: false,
        contactSupport: false,
      })
      return
    }

    setIsInquiring(true)
    setFailure(undefined)
    try {
      const response = await axios.post(`${API_URL}/taxpayers/registration/sso/inquiry`, {
        fiscalId,
      })
      setInquiry(response.data.data as SsoInquiryResult)
      setVerificationNow(Date.now())
      setIdempotencyKey(createIdempotencyKey())
    } catch (error) {
      setInquiry(undefined)
      setFailure(
        readSsoRegistrationFailure(error, 'استعلام انجام نشد. چند لحظه بعد دوباره تلاش کنید.')
      )
    } finally {
      setIsInquiring(false)
    }
  }

  // newlyRegistered: the business was created on this page with the terms box ticked, so its
  // acceptance is recorded for it (an existing business opened from here is not).
  const openBusiness = async (companyId: number, newlyRegistered = false) => {
    setIsSwitching(true)
    setFailure(undefined)
    try {
      await axios.post(`${API_URL}/auth/changeCompany`, {compnyId: companyId})
      if (newlyRegistered && termsAccepted && terms) {
        try {
          await acceptTerms({
            version: terms.version,
            contentHash: terms.contentHash,
            channel: 'registration',
          })
        } catch {
          // Not recorded: the acceptance window asks again on entering the panel.
        }
      }
      // changeCompany answers without the SSO marker; the session read keeps it.
      const {data: user} = await getUserByToken('')
      saveAuth(user as AuthModel)
      setCurrentUser(user as AuthModel)
      navigate('/dashboard', {replace: true})
    } catch (error) {
      setFailure(
        readSsoRegistrationFailure(
          error,
          'کسب‌وکار ثبت شد، ولی باز کردن آن انجام نشد. دوباره روی «ورود به کسب‌وکار» بزنید.'
        )
      )
      setIsSwitching(false)
    }
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!inquiry || busy || !termsAccepted) return
    if (inquiryExpired) {
      setFailure({
        message: 'اعتبار استعلام پایان یافته است؛ دوباره استعلام بگیرید.',
        requiresFreshInquiry: true,
        contactSupport: false,
      })
      return
    }

    setIsSubmitting(true)
    setFailure(undefined)
    try {
      const response = await axios.post(`${API_URL}/taxpayers/registration/sso`, {
        inquiryId: inquiry.inquiryId,
        idempotencyKey,
        fiscalId: inquiry.fiscalId,
      })
      const result = response.data.data as SsoCreateResult
      setCreated(result)
      setIsSubmitting(false)
      await openBusiness(result.companyId, true)
    } catch (error) {
      const nextFailure = readSsoRegistrationFailure(
        error,
        'ثبت کسب‌وکار انجام نشد. دوباره تلاش کنید.'
      )
      if (nextFailure.requiresFreshInquiry) setInquiry(undefined)
      setFailure(nextFailure)
      setIsSubmitting(false)
    }
  }

  const supportContact = (
    <span>
      با پشتیبانی به شماره{' '}
      <a href={`tel:${SUPPORT_PHONE}`} dir='ltr'>
        {SUPPORT_PHONE_LABEL}
      </a>{' '}
      تماس بگیرید
      {!hasNoBusiness && canUseTicketing(currentUser) && (
        <>
          {' '}
          یا <Link to='/apps/ticket-management/new'>تیکت ثبت کنید</Link>
        </>
      )}
      .
    </span>
  )

  return (
    <main className='business-registration' dir='rtl'>
      <header className='business-registration__header'>
        <div>
          <span className='business-registration__eyebrow'>کارپوشه مالیاتی</span>
          <h1>{hasNoBusiness ? 'ثبت اولین کسب‌وکار' : 'افزودن کسب‌وکار با کارپوشه'}</h1>
          <p>شناسه حافظه مالیاتی خود را وارد کنید؛ بقیه اطلاعات از استعلام رسمی خوانده می‌شود.</p>
        </div>
        {hasNoBusiness ? (
          <div className='business-registration__header-actions'>
            <Link to={ONLINE_REGISTRATION_PATH} className='btn btn-light'>
              <i className='las la-arrow-right' aria-hidden='true' /> بازگشت به ثبت‌نام آنلاین
            </Link>
            <RegistrationSignOut />
          </div>
        ) : (
          <Link to='/dashboard' className='btn btn-light'>
            <i className='las la-arrow-right' aria-hidden='true' /> بازگشت به داشبورد
          </Link>
        )}
      </header>

      <div className='business-alert business-alert--info sso-registration__identity' role='note'>
        <i className='las la-id-card' aria-hidden='true' />
        <div>
          <strong>
            ثبت با کد ملی/شناسه ملی{' '}
            <span dir='ltr' className='sso-registration__nid'>
              {currentUser?.sso?.nid}
            </span>{' '}
            متصل به کارپوشه
          </strong>
          <small className='sso-registration__note'>
            فقط حافظه مالیاتی‌ای که به نام همین هویت ثبت شده باشد پذیرفته می‌شود.
          </small>
        </div>
      </div>

      {hasNoBusiness && (
        <div className='business-alert business-alert--warning' role='status'>
          <i className='las la-exclamation-triangle' aria-hidden='true' />
          <div>
            <strong>کسب‌وکاری ثبت نشده است</strong>
            <small className='sso-registration__note'>
              برای شروع صدور صورتحساب، اولین کسب‌وکار خود را ثبت کنید.
            </small>
          </div>
        </div>
      )}

      <form onSubmit={submit} noValidate>
        <section className='business-section' aria-labelledby='sso-fiscal-title'>
          <div className='business-section__heading'>
            <span>۱</span>
            <div>
              <h2 id='sso-fiscal-title'>شناسه حافظه مالیاتی</h2>
              <p>
                شناسه ۶ کاراکتری را از کارپوشه، بخش «حافظه‌های مالیاتی»، بردارید.{' '}
                <a href={FISCAL_ID_GUIDE_URL} target='_blank' rel='noopener noreferrer'>
                  راهنمای دریافت
                </a>
              </p>
            </div>
          </div>
          <div className='fiscal-inquiry-row'>
            <div className='business-field'>
              <label htmlFor='ssoFiscalId'>شناسه یکتای حافظه مالیاتی</label>
              <input
                id='ssoFiscalId'
                dir='ltr'
                maxLength={6}
                autoCapitalize='characters'
                autoComplete='off'
                autoFocus
                placeholder='A1B2C3'
                value={fiscalId}
                disabled={busy || Boolean(created)}
                aria-invalid={Boolean(failure && !failure.existingBusiness)}
                aria-describedby='ssoFiscalId-help'
                onChange={(event) => {
                  setFiscalId(normalizeFiscalIdInput(event.target.value))
                  resetInquiry()
                }}
              />
              <small id='ssoFiscalId-help'>
                حروف بزرگ انگلیسی و رقم؛ فارسی تایپ کنید هم تبدیل می‌شود.
              </small>
            </div>
            <button
              type='button'
              className='btn btn-primary business-inquiry-button'
              disabled={busy || Boolean(created) || !isCompleteFiscalId(fiscalId)}
              onClick={inquire}
            >
              {isInquiring ? (
                <>
                  <span className='spinner-border spinner-border-sm' /> در حال استعلام…
                </>
              ) : (
                <>
                  <i className='las la-search' aria-hidden='true' /> استعلام
                </>
              )}
            </button>
          </div>

          {failure && (
            <div className='business-alert business-alert--error' role='alert'>
              <i className='las la-exclamation-circle' aria-hidden='true' />
              <div>
                <strong>{failure.message}</strong>
                {failure.contactSupport && (
                  <small className='sso-registration__note'>{supportContact}</small>
                )}
                {failure.traceId && (
                  <small className='sso-registration__note'>
                    شناسه پیگیری: <span dir='ltr'>{failure.traceId}</span>
                  </small>
                )}
                {failure.existingBusiness && (
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary mt-3'
                    disabled={busy}
                    onClick={() => openBusiness(failure.existingBusiness!.companyId)}
                  >
                    ورود به «{failure.existingBusiness.name}»
                  </button>
                )}
                {created && (
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary mt-3'
                    disabled={busy}
                    onClick={() => openBusiness(created.companyId, true)}
                  >
                    ورود به کسب‌وکار
                  </button>
                )}
              </div>
            </div>
          )}

          {inquiry && !inquiryExpired && (
            <div className='verified-business' role='status' aria-live='polite'>
              <div className='verified-business__title'>
                <i className='las la-check-circle' aria-hidden='true' />
                <div>
                  <strong>حافظه مالیاتی به نام شماست</strong>
                  <small>این نتیجه تا ۱۰ دقیقه برای ثبت معتبر است.</small>
                </div>
              </div>
              <dl>
                <div>
                  <dt>نام مؤدی</dt>
                  <dd>{inquiry.taxpayerName}</dd>
                </div>
                <div>
                  <dt>نوع مؤدی</dt>
                  <dd>{inquiry.taxpayerTypeTitle}</dd>
                </div>
                <div>
                  <dt>شماره اقتصادی</dt>
                  <dd dir='ltr'>{inquiry.economicCode}</dd>
                </div>
                <div>
                  <dt>شناسه ملی / کد ملی</dt>
                  <dd dir='ltr'>{inquiry.nationalIdentifier}</dd>
                </div>
                <div>
                  <dt>حافظه مالیاتی</dt>
                  <dd dir='ltr'>{inquiry.fiscalId}</dd>
                </div>
                <div>
                  <dt>وضعیت</dt>
                  <dd className='text-success'>فعال</dd>
                </div>
              </dl>
            </div>
          )}
          {inquiryExpired && (
            <div className='business-alert business-alert--warning' role='alert'>
              اعتبار استعلام پایان یافته است. برای ادامه دوباره استعلام بگیرید.
            </div>
          )}
        </section>

        <section className='business-section' aria-labelledby='sso-terms-title'>
          <div className='business-section__heading'>
            <span>۲</span>
            <div>
              <h2 id='sso-terms-title'>قوانین و مقررات</h2>
              <p>پذیرش شما به نام کسب‌وکاری که ثبت می‌شود ثبت خواهد شد.</p>
            </div>
          </div>
          <div className='sso-registration__terms'>
            <div className='form-check form-check-custom form-check-solid'>
              <input
                id='ssoTermsAccepted'
                type='checkbox'
                className='form-check-input'
                checked={termsAccepted}
                disabled={busy || Boolean(created)}
                onChange={(event) => setTermsAccepted(event.target.checked)}
              />
              <label className='form-check-label' htmlFor='ssoTermsAccepted'>
                قوانین و مقررات استفاده از خدمات
                {terms ? ` (${formatTermsVersion(terms.version)})` : ''} را خوانده‌ام و می‌پذیرم.
              </label>
            </div>
            <TermsViewerButton className='fw-semibold'>
              <i className='las la-file-contract fs-4 me-1' aria-hidden='true' />
              خواندن متن قوانین و مقررات
            </TermsViewerButton>
          </div>
        </section>

        <footer className='business-registration__actions'>
          <div>
            <i className='las la-lock' aria-hidden='true' />
            <span>کسب‌وکار به نام شما ساخته می‌شود و مدیر کامل آن خواهید بود.</span>
          </div>
          <button
            type='submit'
            className='btn btn-success btn-lg'
            disabled={!inquiry || inquiryExpired || busy || Boolean(created) || !termsAccepted}
            title={termsAccepted ? undefined : 'ابتدا قوانین و مقررات را بپذیرید.'}
          >
            {isSubmitting || isSwitching ? (
              <>
                <span className='spinner-border spinner-border-sm' />{' '}
                {isSwitching ? 'در حال ورود به کسب‌وکار…' : 'در حال ثبت…'}
              </>
            ) : (
              <>
                <i className='las la-check-double' aria-hidden='true' /> ثبت نهایی کسب‌وکار
              </>
            )}
          </button>
        </footer>
      </form>
    </main>
  )
}

export {SsoBusinessRegistration}
